import './ReportPage.scss';
import 'react-grid-layout/css/styles.css';

import {
  BatterReportChartConfigDto,
  BatterReportChartDataDto,
  BatterSessionReportChartConfigDto,
  BatterSessionReportChartDataDto,
  BatterSingleSessionReportChartConfigDto,
  BatterSingleSessionReportChartDataDto,
  ChartType,
  PitcherReportChartConfigDto,
  PitcherReportChartDataDto,
  PitcherSessionReportChartConfigDto,
  PitcherSessionReportChartDataDto,
  PitcherSingleSessionReportChartConfigDto,
  PitcherSingleSessionReportChartDataDto,
  UmpireReportChartConfigDto,
  UmpireReportChartDataDto,
  UmpireReportForAdminChartConfigDto,
  UmpireReportForAdminChartDataDto,
  UmpireReportForSessionChartConfigDto,
  UmpireReportForSessionChartDataDto,
} from 'types/CodegenReportTypes';
import { chartIdTochartSetup } from 'utilities/chartIdToComponent';
import { Divider, Paper, Typography } from '@trackman/web-shared-components';
import { Layout, Responsive, WidthProvider } from 'react-grid-layout';
import { ReactNode, useMemo, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ChartWrapper, { DRAG_HANDLER_CLASS } from 'components/ChartWrapper/ChartWrapper';
import { useChartTitle } from 'hooks/helperHooks';

const ResponsiveGridLayout = WidthProvider(Responsive);

type Props = {
  page: number;
  header: ReactNode;
  isEditMode: boolean;
  isLoadingData: boolean;
  setEditingChart: (chartId: ChartType | undefined) => void;
  editingChart: ChartType | undefined;
} & (
  | {
      // Player Report - Pitcher
      layout: PitcherReportChartConfigDto | undefined;
      pageChartData: PitcherReportChartDataDto | undefined;
      setTempLayout: (layout: PitcherReportChartConfigDto) => void;
    }
  | {
      // Player Report - Batter
      layout: BatterReportChartConfigDto | undefined;
      pageChartData: BatterReportChartDataDto | undefined;
      setTempLayout: (layout: BatterReportChartConfigDto) => void;
    }
  | {
      // Umpire Report - Admin Overview
      layout: UmpireReportForAdminChartConfigDto | undefined;
      pageChartData: UmpireReportForAdminChartDataDto | undefined;
      setTempLayout: (layout: UmpireReportForAdminChartConfigDto) => void;
    }
  | {
      // Umpire Report - All Sessions
      layout: UmpireReportChartConfigDto | undefined;
      pageChartData: UmpireReportChartDataDto | undefined;
      setTempLayout: (layout: UmpireReportChartConfigDto) => void;
    }
  | {
      // Umpire Report - Single Session
      layout: UmpireReportForSessionChartConfigDto | undefined;
      pageChartData: UmpireReportForSessionChartDataDto | undefined;
      setTempLayout: (layout: UmpireReportForSessionChartConfigDto) => void;
    }
  | {
      // Session Review Report - Pitcher Single
      layout: PitcherSingleSessionReportChartConfigDto | undefined;
      pageChartData: PitcherSingleSessionReportChartDataDto | undefined;
      setTempLayout: (layout: PitcherSingleSessionReportChartConfigDto) => void;
    }
  | {
      // Session Review Report - Pitcher Aggregated
      layout: PitcherSessionReportChartConfigDto | undefined;
      pageChartData: PitcherSessionReportChartDataDto | undefined;
      setTempLayout: (layout: PitcherSessionReportChartConfigDto) => void;
    }
  | {
      // Session Review Report - Batter Single
      layout: BatterSingleSessionReportChartConfigDto | undefined;
      pageChartData: BatterSingleSessionReportChartDataDto | undefined;
      setTempLayout: (layout: BatterSingleSessionReportChartConfigDto) => void;
    }
  | {
      // Session Review Report - Batter Aggregated
      layout: BatterSessionReportChartConfigDto | undefined;
      pageChartData: BatterSessionReportChartDataDto | undefined;
      setTempLayout: (layout: BatterSessionReportChartConfigDto) => void;
    }
);

export const PAGE_CLASS_FOR_PRINTING = 'report-layout-grid-page';

const ReportPage = memo(
  ({ layout, page, header, isEditMode, pageChartData, isLoadingData, setTempLayout, setEditingChart, editingChart }: Props) => {
    const { t } = useTranslation();

    return (
      <>
        <div className='ReportPage-page-indicator'>
          <Divider className='divider' />
          <Typography variant='label' component='div' color='#A0A0A0'>
            {t('page.start', { page })}
          </Typography>
          <Divider className='divider' />
        </div>
        <div className={`${PAGE_CLASS_FOR_PRINTING} ${isEditMode ? '' : 'not-editing'}`}>
          {header}
          {layout && (
            <Grid
              layout={layout}
              isEditMode={isEditMode}
              pageChartData={pageChartData}
              isLoadingData={isLoadingData}
              setTempLayout={setTempLayout}
              setEditingChart={setEditingChart}
              editingChart={editingChart}
            />
          )}
        </div>
      </>
    );
  }
);

type GridProps = {
  layout: Exclude<Props['layout'], undefined>;
  setTempLayout: Props['setTempLayout'];
  isEditMode: boolean;
  isLoadingData: boolean;
  pageChartData: Props['pageChartData'];
  setEditingChart: Props['setEditingChart'];
  editingChart: Props['editingChart'];
};

const Grid = memo(({ layout, setTempLayout, isEditMode, pageChartData, isLoadingData, setEditingChart, editingChart }: GridProps) => {
  const { getChartTitle, getChartSubTitle, getHideChartTitleInGrid } = useChartTitle();

  const handleHideChart = useCallback(
    (chartId: ChartType) => {
      const newLayout = Object.fromEntries(
        Object.entries(layout).map(([key, config]) => [key, config.chartId === chartId ? { ...config, isHidden: true } : config])
      );
      setTempLayout(newLayout as any);
    },
    [layout, setTempLayout]
  );

  const handleLayoutChange = useCallback(
    (newLayoutTiles: Layout[]) => {
      if (!isEditMode) return;

      const newLayout = Object.fromEntries(
        Object.entries(layout).map(([key, chart]) => {
          const tile = newLayoutTiles.find((t) => t.i === chart.chartId);
          return [
            key,
            {
              ...chart,
              chartDrawingInfo: {
                ...chart.chartDrawingInfo,
                position: {
                  x: tile?.x ?? chart.chartDrawingInfo.position.x,
                  y: tile?.y ?? chart.chartDrawingInfo.position.y,
                },
                width: tile?.w ?? chart.chartDrawingInfo.width,
                height: tile?.h ?? chart.chartDrawingInfo.height,
              },
            },
          ];
        })
      );
      setTempLayout(newLayout as any);
    },
    [isEditMode, layout, setTempLayout]
  );

  const charts = useMemo(() => {
    return Object.values(layout)
      .filter((chart) => !chart.isHidden)
      .map((chart) => {
        const { chartId, chartDrawingInfo, ...extraChartData } = chart;
        const setup = chartIdTochartSetup[chartId];

        if (!setup) {
          console.error(`Chart ${chartId} not found`);
          return null;
        }

        const { Chart, dataKey, Sidebar } = setup;
        const isEditingThisChart = editingChart === chartId;
        const isEditingOtherChart = editingChart != null && !isEditingThisChart;

        return (
          <Paper
            key={chartId}
            data-grid={
              {
                i: chartId,
                x: chartDrawingInfo.position.x,
                y: chartDrawingInfo.position.y,
                w: chartDrawingInfo.width,
                h: chartDrawingInfo.height,
                isDraggable: isEditMode && !isEditingOtherChart,
                isResizable: isEditMode && !isEditingOtherChart,
                resizeHandles: ['se', 'sw'],
              } satisfies Layout
            }
            className={isEditingOtherChart ? 'editing-other-chart' : ''}
          >
            <ChartWrapper
              chartTitle={getChartTitle(chartId, extraChartData)}
              chartSubTitle={getChartSubTitle(chartId, extraChartData)}
              hideChartTitle={getHideChartTitleInGrid(chartId)}
              isEditMode={isEditMode}
              isEditingThisChart={isEditingThisChart}
              isEditingOtherChart={isEditingOtherChart}
              isThisChartEditable={Sidebar !== undefined}
              onEditClick={() => setEditingChart(isEditingThisChart ? undefined : chartId)}
              onHideClick={() => handleHideChart(chartId)}
            >
              <Chart chartData={pageChartData?.[dataKey]} isLoading={isLoadingData} extraChartData={extraChartData as never} />
            </ChartWrapper>
          </Paper>
        );
      });
  }, [
    layout,
    isEditMode,
    pageChartData,
    isLoadingData,
    editingChart,
    getChartTitle,
    getChartSubTitle,
    getHideChartTitleInGrid,
    setEditingChart,
    handleHideChart,
  ]);

  return (
    <ResponsiveGridLayout
      className='report-layout-grid'
      cols={{ lg: 6, md: 6, sm: 6, xs: 6, xxs: 6 }}
      rowHeight={120}
      margin={[16, 16]}
      draggableHandle={`.${DRAG_HANDLER_CLASS}`}
      preventCollision={false}
      compactType='vertical'
      useCSSTransforms={false}
      onLayoutChange={handleLayoutChange}
      style={{ transition: 'opacity 300ms ease' }}
    >
      {charts}
    </ResponsiveGridLayout>
  );
});

export default ReportPage;
